import React from 'react';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Heading,
  Button,
  Box,
  Badge,
  Avatar,
  Flex,
  Text,
  Spinner,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { FiSettings, FiArrowRight } from 'react-icons/fi';
import dayjs from 'dayjs';
import xhr from 'xhr';

export default function CobrosListado() {
  const navigate = useNavigate();
  const [summary, setSummary] = React.useState(null);
  const [institutions, setInstitutions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const [summaryRes, institutionsRes] = await Promise.all([
          xhr('/school-usage-charges/summary'),
          xhr('/school-usage-charges/institutions'),
        ]);
        setSummary(summaryRes.data);
        setInstitutions(institutionsRes.data?.data || []);
      } catch (error) {
        console.error('Error fetching cobros:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="lg" />
      </Box>
    );
  }

  return (
    <Box p={4}>
      <Flex justifyContent="space-between" alignItems="center" mb={6}>
        <Heading size="md">Cobros a Instituciones</Heading>
        <Button
          leftIcon={<FiSettings />}
          colorScheme="purple"
          variant="outline"
          onClick={() => navigate('/plataforma/administrador/instituciones/cobros/tarifas')}
        >
          Configurar tarifas
        </Button>
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
        <Box bg="white" borderRadius="lg" boxShadow="sm" p={4}>
          <Stat>
            <StatLabel>Instituciones con deuda</StatLabel>
            <StatNumber>{summary?.institutionsWithDebt ?? 0}</StatNumber>
          </Stat>
        </Box>
        <Box bg="white" borderRadius="lg" boxShadow="sm" p={4}>
          <Stat>
            <StatLabel>Cargos pendientes</StatLabel>
            <StatNumber>{summary?.pendingCharges ?? 0}</StatNumber>
          </Stat>
        </Box>
        <Box bg="white" borderRadius="lg" boxShadow="sm" p={4}>
          <Stat>
            <StatLabel>Total adeudado</StatLabel>
            <StatNumber>${Number(summary?.totalOwed || 0).toLocaleString()} COP</StatNumber>
          </Stat>
        </Box>
      </SimpleGrid>

      <TableContainer bg="white" borderRadius="lg" boxShadow="sm">
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Institución</Th>
              <Th isNumeric>Cargos pendientes</Th>
              <Th isNumeric>Total adeudado</Th>
              <Th>Último pago</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {institutions.map((school) => (
              <Tr key={school.id}>
                <Td>
                  <Flex alignItems="center" gap={3}>
                    <Avatar size="sm" name={school.name} src={school.logo_url} />
                    <Text fontWeight="medium">{school.name}</Text>
                  </Flex>
                </Td>
                <Td isNumeric>
                  {school.pendingCount > 0 ? (
                    <Badge colorScheme="red">{school.pendingCount}</Badge>
                  ) : (
                    <Badge colorScheme="green">0</Badge>
                  )}
                </Td>
                <Td isNumeric fontWeight="medium">
                  ${Number(school.totalOwed || 0).toLocaleString()}
                </Td>
                <Td>
                  {school.lastPayment ? (
                    <Box>
                      <Text fontSize="sm">{dayjs(school.lastPayment.date).format('DD/MM/YYYY')}</Text>
                      <Text fontSize="xs" color="gray.500">
                        ${Number(school.lastPayment.amount || 0).toLocaleString()}
                      </Text>
                    </Box>
                  ) : (
                    <Text fontSize="sm" color="gray.500">
                      —
                    </Text>
                  )}
                </Td>
                <Td>
                  {school.totalOwed > 0 && (
                    <Button
                      size="sm"
                      colorScheme="purple"
                      variant="ghost"
                      rightIcon={<FiArrowRight />}
                      onClick={() => navigate(`/plataforma/administrador/instituciones/cobros/${school.id}`)}
                    >
                      Ver
                    </Button>
                  )}
                </Td>
              </Tr>
            ))}
            {institutions.length === 0 && (
              <Tr>
                <Td colSpan={5} textAlign="center" py={8} color="gray.500">
                  No hay instituciones registradas
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
}
